import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Calendar, MapPin, User, Phone, FileEdit, Save, Clock, Loader2 } from 'lucide-react';
import { useNotifications } from '@/contexts/NotificationContext';
import { siteVisitsAPI } from '@/services/siteVisits';

const SiteVisitBar = ({ project, onUpdate }) => {
  const { addNotification } = useNotifications();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    date: '',
    time: '',
    location: '',
    notes: '',
    assignedTo: '',
    contact: ''
  });

  // Load existing site visit data
  useEffect(() => {
    if (project?.id && project.status === 'Site Visit Scheduled') {
      loadSiteVisitData();
    }
  }, [project?.id, project?.status]);

  const loadSiteVisitData = async () => {
    try {
      setLoading(true);
      const response = await siteVisitsAPI.get(project.id);
      
      if (response.success && response.data) {
        setFormData({
          date: response.data.date || '',
          time: response.data.time || '',
          location: response.data.location || '',
          notes: response.data.notes || '',
          assignedTo: response.data.assigned_to || '',
          contact: response.data.contact || ''
        });
      } else {
        // No data found, open edit mode by default
        setIsEditing(true);
      }
    } catch (error) {
      console.error('Failed to load site visit data:', error);
      setIsEditing(true);
    } finally {
      setLoading(false);
    }
  };

  if (project.status !== 'Site Visit Scheduled') return null;

  const handleSave = async () => {
    try {
      setSaving(true);
      
      const response = await siteVisitsAPI.save(project.id, {
        date: formData.date,
        time: formData.time,
        location: formData.location,
        notes: formData.notes,
        assignedTo: formData.assignedTo,
        contact: formData.contact
      });

      if (response.success) {
        setIsEditing(false);
        addNotification('Site Visit Updated', `Site visit details saved for ${project.projectName}`, 'success');
        
        // Refresh data
        if (onUpdate) {
          onUpdate();
        }
      } else {
        throw new Error(response.error || 'Failed to save');
      }
    } catch (error) {
      console.error('Failed to save site visit:', error);
      addNotification('Error', error.message || 'Failed to save site visit information', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    loadSiteVisitData();
    setIsEditing(false);
  };

  if (loading) {
    return (
      <Card className="border-l-4 border-l-amber-500 bg-amber-50/30 mb-6">
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-amber-600" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-l-4 border-l-amber-500 bg-amber-50/30 mb-6 shadow-sm">
      <CardContent className="p-4">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-amber-600" />
            <h3 className="font-bold text-amber-800">Site Visit Details</h3>
          </div>
          {!isEditing && (
            <Button 
              size="sm" 
              variant="ghost" 
              onClick={() => setIsEditing(true)} 
              className="h-8 text-amber-700 hover:bg-amber-100 hover:text-amber-900"
            >
              <FileEdit className="w-4 h-4 mr-2" /> Edit Details
            </Button>
          )}
        </div>

        {isEditing ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-1">
                <Label className="text-xs text-amber-800">Date</Label>
                <Input 
                  type="date" 
                  value={formData.date} 
                  onChange={(e) => setFormData({...formData, date: e.target.value})}
                  className="bg-white border-amber-200 focus:ring-amber-500"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-amber-800">Time</Label>
                <Input 
                  type="time" 
                  value={formData.time} 
                  onChange={(e) => setFormData({...formData, time: e.target.value})}
                  className="bg-white border-amber-200 focus:ring-amber-500"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-amber-800">Location</Label>
                <Input 
                  placeholder="Address or coordinates"
                  value={formData.location} 
                  onChange={(e) => setFormData({...formData, location: e.target.value})}
                  className="bg-white border-amber-200 focus:ring-amber-500"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label className="text-xs text-amber-800">Assigned To</Label>
                <Input 
                  placeholder="Person or Team"
                  value={formData.assignedTo} 
                  onChange={(e) => setFormData({...formData, assignedTo: e.target.value})}
                  className="bg-white border-amber-200 focus:ring-amber-500"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-amber-800">Contact Number</Label>
                <Input 
                  placeholder="+971..."
                  value={formData.contact} 
                  onChange={(e) => setFormData({...formData, contact: e.target.value})}
                  className="bg-white border-amber-200 focus:ring-amber-500"
                />
              </div>
            </div>

            <div className="space-y-1">
              <Label className="text-xs text-amber-800">Notes</Label>
              <Textarea 
                placeholder="Entry requirements, parking info, etc."
                value={formData.notes} 
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                className="bg-white border-amber-200 focus:ring-amber-500 resize-none"
                rows={3}
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button 
                size="sm" 
                variant="ghost" 
                onClick={handleCancel} 
                className="h-8"
                disabled={saving}
              >
                Cancel
              </Button>
              <Button 
                size="sm" 
                onClick={handleSave} 
                className="h-8 bg-amber-600 hover:bg-amber-700 text-white"
                disabled={saving}
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" /> Save Information
                  </>
                )}
              </Button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div className="flex items-start gap-2">
               <Clock className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
               <div>
                  <span className="block text-xs text-amber-600 font-semibold">Date & Time</span>
                  <span className="text-slate-700">
                    {formData.date ? new Date(formData.date).toLocaleDateString() : 'TBD'}
                    {formData.time && ` at ${formData.time}`}
                  </span>
               </div>
            </div>
            <div className="flex items-start gap-2">
               <MapPin className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
               <div>
                  <span className="block text-xs text-amber-600 font-semibold">Location</span>
                  <span className="text-slate-700 truncate block max-w-[200px]" title={formData.location}>
                    {formData.location || 'Not set'}
                  </span>
               </div>
            </div>
            <div className="flex items-start gap-2">
               <User className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
               <div>
                  <span className="block text-xs text-amber-600 font-semibold">Assigned To</span>
                  <span className="text-slate-700">{formData.assignedTo || 'Unassigned'}</span>
               </div>
            </div>
            <div className="flex items-start gap-2">
               <Phone className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
               <div>
                  <span className="block text-xs text-amber-600 font-semibold">Contact</span>
                  <span className="text-slate-700">{formData.contact || 'None'}</span>
               </div>
            </div>
             {formData.notes && (
               <div className="col-span-full border-t border-amber-200/50 pt-2 mt-2">
                 <span className="text-xs text-amber-600 font-semibold mr-2">Notes:</span>
                 <span className="text-slate-600 italic text-sm">{formData.notes}</span>
               </div>
             )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default SiteVisitBar;
